import { useState, useRef, useEffect, useCallback } from "react";
import apiClient from "../../../shared/API/apiClient";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from "recharts";

import { getLocationDistribution } from "../../../services/locationDistributionService";
import { useReportingYear } from "../../../context/ReportingYearContext";

const TrendUpIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
    <path d="M3 17l6-6 4 4 7-7" stroke="currentColor" strokeWidth="2" />
  </svg>
);

const ByLocationPage = () => {
  const {
    reportingYear,
    setReportingYear,
    reportingYearId,
  } = useReportingYear();

  const [legalEntities, setLegalEntities] = useState([]);
  const [selectedEntity, setSelectedEntity] = useState("");

  const formatISO = (d) => d.toISOString().split("T")[0];

  const parseFinancialYear = (fy) => {
    if (!fy) return {};

    const parts = fy.split("-");

    const startYear = Number(parts[0]);

    let endYear;

    if (parts[1]?.length === 2) {
      endYear = Number(`20${parts[1]}`);
    } else {
      endYear = Number(parts[1]);
    }

    return { startYear, endYear };
  };

  const getDefaultDates = (fy) => {
    const { startYear, endYear } = parseFinancialYear(fy);

    return {
      from: `${startYear}-04-01`,
      to: `${endYear}-03-31`,
    };
  };

  const getFinancialYearDates = (fy) => {
    const { startYear, endYear } = parseFinancialYear(fy);

    return {
      minDate: `${startYear}-04-01`,
      maxDate: `${endYear}-03-31`,
    };
  };

  const getFYFromDate = (dateStr) => {
    const date = new Date(dateStr);

    const year = date.getFullYear();
    const month = date.getMonth() + 1;

    let startYear;
    let endYear;

    if (month >= 4) {
      startYear = year;
      endYear = year + 1;
    } else {
      startYear = year - 1;
      endYear = year;
    }

    return `${startYear}-${String(endYear).slice(2)}`;
  };

  const formatDisplay = (d) =>
    new Date(d).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });

  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [activeRange, setActiveRange] = useState(null);

  const { minDate, maxDate } = getFinancialYearDates(reportingYear);

  const [compareFrom, setCompareFrom] = useState("");
  const [compareTo, setCompareTo] = useState("");

  const fromRef = useRef();
  const toRef = useRef();
  const compareFromRef = useRef();
  const compareToRef = useRef();

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  const ranges = ["This Month", "Last Month", "This Quarter", "This Year"];

  const filterMap = {
    "This Month": "THIS_MONTH",
    "Last Month": "LAST_MONTH",
    "This Quarter": "THIS_QUARTER",
    "This Year": "THIS_YEAR",
  };

  const [appliedFilter, setAppliedFilter] = useState({
    filter: null,
    fromDate: null,
    toDate: null,
  });

  const openPicker = (ref) => {
    if (!ref.current) return;
    if (ref.current.showPicker) ref.current.showPicker();
    else ref.current.click();
  };

  //API Legal entities

  useEffect(() => {
    const fetchLegalEntities = async () => {
      if (!reportingYearId) return;

      try {
        const res = await apiClient.get(`/api/legal-entities-by-year`, {
          params: {
            reporting_year_id: reportingYearId,
          },
        });

        if (res.data.code === 200) {
          setLegalEntities(res.data.data || []);
        } else {
          setLegalEntities([]);
        }
      } catch (err) {
        console.error("Legal entities API error:", err);
        setLegalEntities([]);
      }
    };

    fetchLegalEntities();
  }, [reportingYearId]);

  useEffect(() => {
    if (!reportingYear) return;

    const defaults = getDefaultDates(reportingYear);

    setFromDate(defaults.from);
    setToDate(defaults.to);

    setActiveRange(null);

    setAppliedFilter({
      filter: null,
      fromDate: defaults.from,
      toDate: defaults.to,
    });
  }, [reportingYear]);

  // API CALL
  useEffect(() => {
    if (!reportingYearId || !selectedEntity) return;

    let isMounted = true;

    const fetchData = async () => {
      setLoading(true);

      try {
        const res = await getLocationDistribution(
          reportingYear,
          appliedFilter.filter ?? "",
          appliedFilter.fromDate ?? "",
          appliedFilter.toDate ?? "",
          selectedEntity,
          reportingYearId
        );

        if (!isMounted) return;

        console.log("API RESPONSE:", res?.data);

        if (res?.data?.status === "success") {
          setData(res?.data?.locations || []);
        } else {
          setData([]);
        }
      } catch (err) {
        if (!isMounted) return;

        console.error("Location API Error:", err);
        setData([]);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      isMounted = false;
    };
  }, [reportingYear, appliedFilter, selectedEntity, reportingYearId]);



  useEffect(() => {
    if (selectedEntity) {
      setLoading(false);
    }
  }, [selectedEntity]);

  {loading && (
      <div className="dash-loader-wrapper">
        <div className="dash-loader"></div>
        <p className="dash-loader-text">Loading ByLocation...</p>
      </div>
    )}

  // CHART DATA (SAFE)
  const locationData = data.map((item) => ({
    name: item.site_name,
    scope1: Number(item.scope1_emissions),
    scope2: Number(item.scope2_emissions),
  }));

  //No data component

  const NoData = ({ message }) => (
    <div
      style={{
        position: "absolute",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        color: "#999",
        fontSize: 13,
        textAlign: "center",
        width: "100%",
        pointerEvents: "none",
      }}
    >
      {message}
    </div>
  );

  const CalendarIcon = () => (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="3" y="4" width="18" height="18" rx="2" />
      <line x1="16" y1="2" x2="16" y2="6" />
      <line x1="8" y1="2" x2="8" y2="6" />
      <line x1="3" y1="10" x2="21" y2="10" />
    </svg>
  );

  return (
    <div className="dash-page">
      <div className="dash-page-title">Location-wise Distribution</div>
      <div className="dash-page-subtitle">
        Internal report — not part of SEBI BRSR filing
      </div>

      <div className="dash-tag">Internal Use Only</div>

      <div className="filter-card">
        <div className="filter-group">
          <label>Reporting Year *</label>

          <input
            className="filter-input"
            value={reportingYear || ""}
            disabled
          />
        </div>

        <div className="filter-group">
          <label>Legal Entity *</label>

          <select
            className="filter-input"
            value={selectedEntity}
            onChange={(e) => {
              setSelectedEntity(e.target.value);
            }}
          >
            <option value="">Select</option>

            {legalEntities.map((entity) => (
              <option key={entity.entity_id} value={entity.entity_id}>
                {entity.entity_name}
              </option>
            ))}
          </select>
        </div>
      </div>

      
       {/* EMPTY STATE */}
      {!selectedEntity && (
        <div className="st-empty-entity">
          <div className="st-empty-entity__icon">
            <i className="fa-solid fa-building" />
          </div>

          <div className="st-empty-entity__title">
            Select a Legal Entity to continue
          </div>

          <div className="st-empty-entity__sub">
            Select a legal entity above to view Location-wise Distribution,
            emission trends, location-wise summaries, and analytics reports.
          </div>
        </div>
      )}

      {selectedEntity && (
        <>

      {/* DATE RANGE */}
      <div className="dash-card" style={{ marginTop: 20 }}>
        <div className="dash-date-top">
          <div className="dash-date-title">
            <CalendarIcon />
            Report Date Range
          </div>

          <div className="dash-range-pills">
            {ranges.map((r) => (
              <button
                key={r}
                className={`range-pill ${activeRange === r ? "active" : ""}`}
                onClick={() => {
                  setActiveRange(r);

                  const today = new Date();

                  let from = null;
                  let to = null;

                  if (r === "This Month") {
                    from = formatISO(
                      new Date(today.getFullYear(), today.getMonth(), 1)
                    );
                    to = formatISO(today);
                  }

                  if (r === "Last Month") {
                    const first = new Date(
                      today.getFullYear(),
                      today.getMonth() - 1,
                      1
                    );
                    const last = new Date(
                      today.getFullYear(),
                      today.getMonth(),
                      0
                    );
                    from = formatISO(first);
                    to = formatISO(last);
                  }

                  if (r === "This Year") {
                    const first = new Date(today.getFullYear(), 0, 1);
                    from = formatISO(first);
                    to = formatISO(today);
                  }

                  if (r === "This Quarter") {
                    const quarter = Math.floor(today.getMonth() / 3);
                    const first = new Date(today.getFullYear(), quarter * 3, 1);
                    from = formatISO(first);
                    to = formatISO(today);
                  }

                  setFromDate(from);
                  setToDate(to);

                  // ONLY filter, no date
                  setAppliedFilter({
                    filter: filterMap[r],
                    fromDate: null,
                    toDate: null,
                  });
                }}
              >
                {r}
              </button>
            ))}
          </div>
        </div>

        <div className="dash-date-row">
          {/* FROM */}
          <div className="dash-date-field">
            <div className="dash-date-field-label">From Date</div>
            <div
              className="dash-date-input"
              style={{ position: "relative" }}
              onClick={() => openPicker(fromRef)}
            >
              <img
                src="https://cdn-icons-png.flaticon.com/512/747/747310.png"
                alt="calendar"
                className="dash-date-icon-img"
              />
              <span>{formatDisplay(fromDate)}</span>

              <input
                ref={fromRef}
                type="date"
                value={fromDate}
                min={minDate}
                max={maxDate}
                onChange={(e) => {
                  const value = e.target.value;

                  setFromDate(value);
                  setActiveRange(null);

                  const fy = getFYFromDate(value);

                  setReportingYear(fy);
                }}
                style={{
                  position: "absolute",
                  opacity: 0,
                  pointerEvents: "none",
                }}
              />
            </div>
          </div>

          {/* TO */}
          <div className="dash-date-field">
            <div className="dash-date-field-label">To Date</div>
            <div
              className="dash-date-input"
              style={{ position: "relative" }}
              onClick={() => openPicker(toRef)}
            >
              <img
                src="https://cdn-icons-png.flaticon.com/512/747/747310.png"
                alt="calendar"
                className="dash-date-icon-img"
              />
              <span>{formatDisplay(toDate)}</span>
              <input
                ref={toRef}
                type="date"
                value={toDate}
                min={minDate}
                max={maxDate}
                onChange={(e) => {
                  const value = e.target.value;

                  setToDate(value);
                  setActiveRange(null);

                  const fy = getFYFromDate(value);

                  setReportingYear(fy);
                }}
                style={{
                  position: "absolute",
                  opacity: 0,
                  pointerEvents: "none",
                }}
              />
            </div>
          </div>

          <div className="dash-date-applied">
            <div
              className="apply-btn-icon"
              onClick={() => {
                setAppliedFilter({
                  filter: null, // ❗ always null
                  fromDate,
                  toDate,
                });
              }}
              style={{ cursor: "pointer" }}
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="11" cy="11" r="8" />
                <path d="m21 21-4.3-4.3" />
              </svg>
            </div>
          </div>

          <div className="dash-divider"></div>

          {/* COMPARE */}
          <div className="dash-date-field">
            <div className="dash-date-field-label">
              Compare From <span className="optional">(optional)</span>
            </div>
            <div
              className="dash-date-input muted"
              style={{ position: "relative" }}
              onClick={() => openPicker(compareFromRef)}
            >
              <img
                src="https://cdn-icons-png.flaticon.com/512/747/747310.png"
                alt="calendar"
                className="dash-date-icon-img"
              />
              <span>
                {compareFrom ? formatDisplay(compareFrom) : "Select date"}
              </span>

              <input
                ref={compareFromRef}
                type="date"
                value={compareFrom}
                onChange={(e) => setCompareFrom(e.target.value)}
                style={{
                  position: "absolute",
                  opacity: 0,
                  pointerEvents: "none",
                }}
              />
            </div>
          </div>

          <div className="dash-date-field">
            <div className="dash-date-field-label">
              Compare To <span className="optional">(optional)</span>
            </div>
            <div
              className="dash-date-input muted"
              style={{ position: "relative" }}
              onClick={() => openPicker(compareToRef)}
            >
              <img
                src="https://cdn-icons-png.flaticon.com/512/747/747310.png"
                alt="calendar"
                className="dash-date-icon-img"
              />
              <span>
                {compareTo ? formatDisplay(compareTo) : "Select date"}
              </span>

              <input
                ref={compareToRef}
                type="date"
                value={compareTo}
                onChange={(e) => setCompareTo(e.target.value)}
                style={{
                  position: "absolute",
                  opacity: 0,
                  pointerEvents: "none",
                }}
              />
            </div>
          </div>
        </div>

        <div className="dash-date-applied-range">
          {formatDisplay(fromDate)} — {formatDisplay(toDate)}
        </div>
      </div>

      {/* CHART */}
      <div
        className="dash-card"
        style={{ marginTop: 20, position: "relative" }}
      >
        <h5 className="flex items-center gap-2">
          <TrendUpIcon /> Scope 1 vs Scope 2 by Location
        </h5>
        <br />

        <ResponsiveContainer width="100%" height={300}>
          {locationData.length ? (
            <BarChart className="chartSize" data={locationData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" angle={-20} textAnchor="end" height={70} />
              <YAxis />
              <Tooltip />
              <Bar dataKey="scope1" stackId="a" fill="#2e944b" />
              <Bar dataKey="scope2" stackId="a" fill="#f59e0b" />
            </BarChart>
          ) : (
            <NoData message="No Scope 1 vs Scope 2 by Location data available" />
          )}
        </ResponsiveContainer>
      </div>

      {/* TABLE */}
      <div className="dash-card" style={{ marginTop: 20 }}>
        <h5>Trend Data</h5>
        <br />

        <table className="dash-table">
          <thead>
            <tr>
              <th>LOCATION</th>
              <th>SCOPE 1(tCO₂e)</th>
              <th>SCOPE 2(tCO₂e)</th>
              <th>TOTAL(tCO₂e)</th>
              <th>% SHARE</th>
            </tr>
          </thead>
          <tbody>
            {data.map((d, i) => (
              <tr key={i} className="mobile-row">
                <td data-label="Site">{d.site_name}</td>
                <td data-label="Scope 1">{d.scope1_emissions}</td>
                <td data-label="Scope 2">{d.scope2_emissions}</td>
                <td data-label="Total">
                  <b>{d.total_emissions}</b>
                </td>
                <td data-label="%">{d.percent_of_total}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      </>
      )}
    </div>
  );
};

export default ByLocationPage;
